/**
 * Centralized Utilities for BLKPacks Frontend
 * Contains shared functions, components, and configurations
 */
import React from "react";
import axios from "axios";
import { RefreshCw } from "lucide-react";

// ============== API CONFIGURATION ==============
// Production backend URL - used by Capacitor native apps and as fallback
const PRODUCTION_URL = 'https://www.blkpacks.fun';

// Dynamically determine the backend URL based on current hostname
const getBackendUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // Production domains - use same origin
    if (hostname === 'www.blkpacks.fun' || hostname === 'blkpacks.fun') {
      return window.location.origin;
    }
    
    // Preview/staging domains (Emergent)
    if (hostname.includes('emergentagent.com')) {
      return window.location.origin;
    }
    
    // Capacitor native app loads from https://localhost (iosScheme: 'https')
    // OR local dev on localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const envUrl = process.env.REACT_APP_BACKEND_URL;
      // NEVER use Emergent preview URLs in native/local builds — always production
      if (!envUrl || envUrl.includes('emergentagent.com') || envUrl.includes('preview.')) {
        return PRODUCTION_URL;
      }
      return envUrl;
    }
  }
  
  return process.env.REACT_APP_BACKEND_URL || PRODUCTION_URL;
};

export const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

// ============== CANONICAL SHARE ORIGIN ==============
// Always returns the user-facing brand domain for any shared URL —
// regardless of whether the user happens to be browsing through the
// underlying Emergent host alias (blkpacks-fun.emergent.host) or any
// other hostname production happens to be reachable from. Without this,
// share buttons would leak ugly internal hostnames into iMessage / SMS
// link previews (the og:url + canonical tags are correct on the backend,
// but iMessage scrapes from the URL the SENDER shared, not where it
// resolves to).
//
// USE THIS for anything a user will see / paste / send:
//   - Collectible share buttons
//   - Referral links
//   - Live break share links
//   - Calculator / verify deep links
//
// DO NOT use this for API calls, Stripe success/cancel URLs, or any URL
// that must be same-origin with the current browsing session — those
// still want window.location.origin.
const CANONICAL_SHARE_ORIGIN = 'https://blkpacks.fun';
export const getShareOrigin = () => {
  if (typeof window === 'undefined') return CANONICAL_SHARE_ORIGIN;
  const h = window.location.hostname;
  // Production brand domain → keep canonical (already correct, but be explicit)
  if (h === 'blkpacks.fun' || h === 'www.blkpacks.fun') return CANONICAL_SHARE_ORIGIN;
  // Emergent's underlying deployment host (alias for production) →
  // ALWAYS rewrite to the brand domain so users never see emergent.host
  if (h.endsWith('.emergent.host')) return CANONICAL_SHARE_ORIGIN;
  // Native Capacitor app (iosScheme:'https' → 'localhost') → use brand domain
  if (h === 'localhost' || h === '127.0.0.1') return CANONICAL_SHARE_ORIGIN;
  // Preview / staging on emergentagent.com → use the actual origin so QA
  // can test shares end-to-end without hitting production
  return window.location.origin;
};

// ============== LOGIN REDIRECT HELPER ==============
// Centralized "send the user to /login but remember where they were" helper.
// Without this, every navigate("/login") call dumps the user on /profile after
// sign-in (LoginPage's hardcoded post-auth destination). Pass the current
// pathname (+ search) and the user lands back on the exact page they tried to
// access. Reads cleanly: `navigateToLogin(navigate, location.pathname)`.
//
// `nextPath` is optional — falls back to `window.location.pathname + search`
// when omitted so callers without a useLocation() handy still work.
export const navigateToLogin = (navigate, nextPath) => {
  let target = nextPath;
  if (!target && typeof window !== 'undefined') {
    target = window.location.pathname + (window.location.search || '');
  }
  if (!target || target === '/login' || target.startsWith('/login?')) {
    navigate('/login');
    return;
  }
  navigate(`/login?next=${encodeURIComponent(target)}`);
};

// Debug: Log API URL on load
if (typeof window !== 'undefined') {
  console.log('🔗 BLK PACKS API URL:', API);
  console.log('🌐 Current hostname:', window.location.hostname);
  console.log('🌐 Current origin:', window.location.origin);
}

/**
 * Helper to construct proper media URL for video/image previews
 * Handles local uploads and external URLs consistently
 * @param {string} url - The URL from the database
 * @returns {string} - Full URL ready for display
 */
export const getMediaUrl = (url) => {
  if (!url || url === 'None' || url === 'null' || url === 'undefined') return '';
  
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (!trimmed) return '';
  
  // Detect if running inside Capacitor native app (iOS/Android)
  const isNative = window?.Capacitor?.isNativePlatform?.() || false;
  
  // Already a full URL (CDN or external)
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    // R2 CORS is configured (access-control-allow-origin: *) so CDN URLs
    // work directly in WKWebView. No backend proxy needed.
    return trimmed;
  }
  
  // Extract filename to check if it's a private file (KYC or receipts)
  const filename = trimmed.split('/').pop() || '';
  const isPrivateFile = filename.startsWith('kyc_') || filename.startsWith('receipt_');
  
  // Private files (KYC, receipts) MUST go through the backend API, never CDN
  if (isPrivateFile) {
    if (trimmed.startsWith('/api/uploads/')) {
      return `${BACKEND_URL}${trimmed}`;
    }
    if (trimmed.startsWith('/uploads/')) {
      return `${BACKEND_URL}/api${trimmed}`;
    }
    return `${BACKEND_URL}/api/uploads/${filename}`;
  }
  
  // Local bundled assets (public folder) — serve directly, never via CDN/proxy
  if (trimmed.startsWith('/images/') || trimmed.startsWith('/icons/') || trimmed.startsWith('/fonts/') || trimmed.startsWith('/static/')) {
    return trimmed;
  }
  
  // Route paths through CDN — R2 CORS allows direct access from native too
  const CDN = process.env.REACT_APP_CDN_URL;
  if (CDN) {
    let cdnPath = trimmed;
    if (trimmed.startsWith('/api/uploads/')) {
      cdnPath = trimmed.replace('/api/uploads/', '/uploads/');
    } else if (!trimmed.startsWith('/')) {
      cdnPath = `/uploads/${trimmed}`;
    }
    return `${CDN}${cdnPath}`;
  }
  
  // Fallback to backend if no CDN configured
  if (trimmed.startsWith('/api/uploads/')) {
    return `${BACKEND_URL}${trimmed}`;
  }
  if (trimmed.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${trimmed}`;
  }
  if (trimmed.startsWith('/')) {
    return `${BACKEND_URL}${trimmed}`;
  }
  return `${BACKEND_URL}/api/uploads/${trimmed}`;
};

// Authenticated API helper - creates axios instance with auth header
export const authApi = (token) => {
  const instance = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000 // 30 seconds for admin operations
  });
  // Global 429 / rate-limit guard. Without this, a single 429 from a
  // background fetch can bubble through React state and trip the
  // ErrorBoundary. We log it, dispatch a toast-friendly window event
  // (so callers can show a soft message if they want), and re-reject.
  instance.interceptors.response.use(
    (r) => r,
    (err) => {
      const status = err?.response?.status;
      if (status === 429) {
        const retryAfter = err?.response?.headers?.["retry-after"];
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("blk:rate-limit", { detail: { retryAfter } }));
        }
      }
      return Promise.reject(err);
    }
  );
  return instance;
};

// ============== SHARED COMPONENTS ==============

// LoadingSpinner - Reusable loading indicator
export const LoadingSpinner = ({ text = "Loading...", size = 48 }) => (
  <div className="flex flex-col items-center justify-center py-20">
    <RefreshCw className="animate-spin text-amber-400 mb-4" size={size} />
    <p className="text-gray-400">{text}</p>
  </div>
);

// Inline loading spinner for buttons/smaller areas
export const InlineSpinner = ({ size = 18 }) => (
  <RefreshCw className="animate-spin" size={size} />
);

// ============== RARITY SYSTEM ==============

export const RARITY_MULTIPLIERS = {
  'COMMON': 1.0,
  'RARE': 1.5,
  'EPIC': 2.0,
  'LEGENDARY': 3.0
};

export const RARITY_BONUS = {
  'COMMON': 1.00,
  'RARE': 3.50,
  'EPIC': 8.00,
  'LEGENDARY': 20.00
};

export const RARITY_COLORS = {
  'COMMON': { bg: 'bg-gray-600', text: 'text-gray-300', border: 'border-gray-500' },
  'RARE': { bg: 'bg-blue-600', text: 'text-blue-300', border: 'border-blue-500' },
  'EPIC': { bg: 'bg-amber-600', text: 'text-amber-300', border: 'border-amber-500' },
  'LEGENDARY': { bg: 'bg-gradient-to-r from-orange-500 to-orange-600', text: 'text-orange-200', border: 'border-orange-500' }
};

// ============== SERIAL NUMBER CALCULATIONS ==============

/**
 * Calculate serial number bonus based on position
 * Lower serials = higher value, with #1 being most valuable
 */
export const calculateSerialBonus = (serialNumber, totalEditions) => {
  if (!serialNumber) return 0;
  
  const MAX_SERIAL_BONUS = 50.00;
  
  // Special bonuses for ultra-rare serials (works even without total_editions)
  if (serialNumber === 1) return MAX_SERIAL_BONUS;
  if (serialNumber <= 5) return MAX_SERIAL_BONUS * 0.7 * (1 - (serialNumber - 1) / 5);
  if (serialNumber <= 10) return MAX_SERIAL_BONUS * 0.5 * (1 - (serialNumber - 5) / 10);
  if (serialNumber <= 100) return MAX_SERIAL_BONUS * 0.2 * (1 - (serialNumber - 10) / 90);
  
  if (!totalEditions || totalEditions <= 0) return 0;
  
  // For regular serials, use curve based on position
  const position = serialNumber / totalEditions;
  if (position <= 0.01) return 10.00;
  if (position <= 0.05) return 5.00;
  if (position <= 0.10) return 2.50;
  if (position <= 0.25) return 1.00;
  
  return 0;
};

/**
 * Get serial tier label and styling
 */
export const getSerialTier = (serialNumber, totalEditions, collectible) => {
  if (!serialNumber) return null;
  // Hobby drops never expose serial tiers (GENESIS / TOP 5 / etc) — these
  // are rarity-tier signals forbidden under the licensed-IP restriction.
  // Pack-position (#7 of 21) is shown elsewhere instead.
  if (collectible && (collectible.is_hobby || collectible.is_hobby_drop)) return null;
  if (serialNumber === 1) return { tier: 'GENESIS', color: 'text-amber-300', bg: 'bg-amber-500/20', glow: 'shadow-amber-500/50' };
  if (serialNumber <= 5) return { tier: 'TOP 5', color: 'text-yellow-300', bg: 'bg-yellow-500/20', glow: 'shadow-yellow-500/40' };
  if (serialNumber <= 10) return { tier: 'TOP 10', color: 'text-amber-400', bg: 'bg-amber-500/20', glow: 'shadow-amber-500/40' };
  if (serialNumber <= 100) return { tier: 'TOP 100', color: 'text-blue-400', bg: 'bg-blue-500/20', glow: 'shadow-blue-500/30' };
  
  if (!totalEditions) return null;
  const position = serialNumber / totalEditions;
  if (position <= 0.01) return { tier: 'TOP 1%', color: 'text-green-400', bg: 'bg-green-500/20', glow: 'shadow-green-500/30' };
  if (position <= 0.05) return { tier: 'TOP 5%', color: 'text-teal-400', bg: 'bg-teal-500/20', glow: '' };
  if (position <= 0.10) return { tier: 'TOP 10%', color: 'text-amber-400', bg: 'bg-amber-500/20', glow: '' };
  
  return null;
};

/**
 * Calculate total collectible value including metal, rarity, and serial bonuses
 */
export const calculateCollectibleValue = (collectible, metalPrices) => {
  if (!collectible || !metalPrices) return null;

  // HOBBY DROPS — physical pack hunts under licensing restriction.
  // No metal backing, no rarity multiplier, no serial bonus, no $ value.
  if (collectible.is_hobby || collectible.is_hobby_drop) {
    return {
      metalValue: "0.00",
      rarityBonus: "0.00",
      serialBonus: "0.00",
      totalValue: "0.00",
      serialTier: null,
      isSealed: !!collectible.is_sealed,
      isHobby: true,
    };
  }

  const { rarity, metal_type, edition_number, total_editions, is_sealed } = collectible;
  
  // Base metal value
  const basePrice = metal_type === 'gold' 
    ? metalPrices.gold_blkback_price 
    : metalPrices.silver_blkback_price;
  
  // Serial bonus (0 if sealed)
  const serialBonus = is_sealed ? 0 : calculateSerialBonus(edition_number, total_editions);
  
  // Rarity bonus
  const rarityBonus = RARITY_BONUS[rarity] || 0;
  const rarityMultiplier = RARITY_MULTIPLIERS[rarity] || 1.0;
  
  // Calculate total
  const metalValue = basePrice * rarityMultiplier;
  const totalValue = metalValue + rarityBonus + serialBonus;
  
  return {
    metalValue: metalValue.toFixed(2),
    rarityBonus: rarityBonus.toFixed(2),
    serialBonus: serialBonus.toFixed(2),
    totalValue: totalValue.toFixed(2),
      serialTier: is_sealed ? null : getSerialTier(edition_number, total_editions, collectible),
    isSealed: is_sealed
  };
};

// ============== FORMATTERS ==============

/**
 * Format currency with $ symbol
 */
export const formatCurrency = (amount, decimals = 2) => {
  if (amount === null || amount === undefined) return '$0.00';
  return `$${parseFloat(amount).toFixed(decimals)}`;
};

/**
 * Format date to readable string
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  });
};

/**
 * Format date with time
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Get relative time (e.g., "2 hours ago")
 */
export const getRelativeTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return formatDate(dateString);
};

// ============== VALIDATION ==============

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Validate referral code format (8 alphanumeric chars)
 */
export const isValidReferralCode = (code) => {
  return /^[A-Z0-9]{8}$/.test(code?.toUpperCase());
};

// ============== STATUS HELPERS ==============

export const USER_STATUS_CONFIG = {
  rookie: { label: "🌱 ROOKIE", color: "bg-gray-600", desc: "Just starting out" },
  starter: { label: "⚡ STARTER", color: "bg-blue-600", desc: "Getting warmed up" },
  rising_star: { label: "🌟 RISING STAR", color: "bg-amber-600", desc: "Making moves" },
  all_star: { label: "⭐ ALL STAR", color: "bg-orange-600", desc: "Top performer" },
  veteran: { label: "🏆 VETERAN", color: "bg-orange-600", desc: "Seasoned collector" },
  goat: { label: "🐐 GOAT", color: "bg-gradient-to-r from-orange-500 to-orange-500", desc: "Greatest of all time" }
};

export const getStatusConfig = (status) => {
  return USER_STATUS_CONFIG[status] || USER_STATUS_CONFIG.rookie;
};

// ============== MISC HELPERS ==============

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Generate unique ID
 */
export const generateId = () => {
  return Math.random().toString(36).substring(2, 15);
};

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

/**
 * Format axios/FastAPI error responses for safe display in alert()/toast.error().
 * Handles the Pydantic-validation case where `detail` is an array of objects
 * (otherwise stringifies to "[object Object],[object Object]").
 */
export const formatApiError = (err, fallback = "Request failed") => {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        const loc = Array.isArray(d?.loc)
          ? d.loc.filter((p) => p !== "body").join(".")
          : "";
        const msg = d?.msg || d?.message || JSON.stringify(d);
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join("\n");
  }
  if (typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return String(detail) || fallback;
};

